import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { plural, t } from '@lingui/macro';

import Modal from '@rover/components/src/OverflowTextModal/Modal';
import { SearchParamsPetType } from '@rover/constants/src/translations/sitterProfilePage.constants';
import type { Icon as IconType } from '@rover/icons';
import ArrowLeft from '@rover/icons/svg/arrow-left.svg';
import Calendar from '@rover/icons/svg/calendar.svg';
import ExpandSmall from '@rover/icons/svg/expand-small.svg';
import Rebook from '@rover/icons/svg/rebook.svg';
import RecurringCalendar from '@rover/icons/svg/recurring-calendar.svg';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import A11yHidden from '@rover/kibble/styles/A11yHidden';
import type { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import type { EnhancedPersonSitterProfileServiceAddOn } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileServiceAddOn';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { ServiceAddOnTypeSlugs as AddOnSlugs } from '@rover/types/src/ServiceAddOn';
import {
  isCatOnlyAvailablePetTypesBasedOnPetTypes,
  isCatOnlyServiceBasedOnAcceptedPetTypes,
} from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { RevampProfileContext } from '../../hooks/useRevampProfileContext';
import AutoHeight from '../../utils/AutoHeight/AutoHeight';
import RevampCancellationPolicy from '../RevampServicesCta/RevampCancellationPolicy';

import Separator from './Separator';
import ServiceAddOns from './ServiceAddOns';
import ServiceHeader from './ServiceHeader';
import { filterAddons } from './utils';

export type AcceptingNewCustomerProps = {
  text: string;
  icon: IconType;
};

const ArrowIcon = (): JSX.Element => <ArrowLeft width="24px" height="24px" />;
const MoreIcon = (): JSX.Element => <ExpandSmall fill="#2E67D1" />;
const NewCustomersIcon = ({ icon: CostumerIcon }: { icon: IconType }): JSX.Element => {
  return <CostumerIcon width={16} height={16} aria-hidden />;
};

export const AdditionalInfo = ({
  onBack,
  title,
  content,
}: {
  onBack: () => void;
  title: string;
  content: string;
}): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  // Replace "-" with "to" for screen reader users
  const [messageSection, dateSection] = content.split(':');
  const to = t({
    message: `to`,
    comment:
      'Used to split dates, for example: "Jan 6 to 25, 2024", or "Dec 21, 2023 to Jan 1, 2024"',
  });
  const newDateSection = dateSection ? dateSection.split('-').join(` ${to} `) : '';
  const a11yContent = messageSection.concat(newDateSection);

  return (
    <Box ref={ref} webProps={{ tabIndex: -1 }} aria-label={t`More information dialog`}>
      <Flex justifyContent="start" alignItems="center" alignSelf="stretch" pb="4x">
        <Box flexShrink="0">
          <Button
            alignSelf="flex-start"
            variant="flat"
            size="small"
            icon={ArrowIcon}
            onClick={onBack}
            data-testid="back-arrow"
            aria-label={`${t`Go back to additional service rates`}`}
            p="0x"
          />
        </Box>
        <Heading size="300" mb="0x" ml="2x">
          {title}
        </Heading>
      </Flex>
      {/* @ts-expect-error */}
      <Paragraph pb="6x" size="200" dangerouslySetInnerHTML={{ __html: content }} aria-hidden />
      <Paragraph sx={{ A11yHidden }}>{a11yContent}</Paragraph>
    </Box>
  );
};

const AdditionalRatesSection = ({
  services,
  petType,
  priceTransparencyLevel,
  showSittersRates = true,
  isSitterInPetCareModesExperiment = false,
  renderTrigger,
  isRevampVersion = false,
  isCtaModal = false,
}: {
  services: EnhancedPersonSitterProfileService[];
  petType?: SearchParamsPetType;
  priceTransparencyLevel?: PriceTransparencyLevel;
  showSittersRates?: boolean;
  isSitterInPetCareModesExperiment?: boolean;
  renderTrigger?: (toggleModal: () => void) => JSX.Element;
  isRevampVersion?: boolean;
  isCtaModal?: boolean;
}): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [moreInfoTitle, setMoreInfoTitle] = useState<string>();
  const [moreInfoContent, setMoreInfoContent] = useState<string>();
  const [isMoreServicesOpen, setIsMoreServicesOpen] = useState(false);

  const revampContext = useContext(RevampProfileContext);
  const serviceType = revampContext?.serviceType;

  const serviceFiltered = services.filter((service) => {
    return service.slug === serviceType;
  });

  const otherServices = services.filter((service) => {
    return service.slug !== serviceType;
  });

  const servicesToRender = serviceType && isCtaModal ? serviceFiltered : services;

  const brandPets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandPets);

  const toggleModal = (): void => {
    setIsModalOpen((value) => !value);
    setIsMoreServicesOpen(false);
    setMoreInfoTitle(undefined);
    setMoreInfoContent(undefined);
  };

  const handleMoreInfoClick = (addOn: EnhancedPersonSitterProfileServiceAddOn): void => {
    setMoreInfoTitle(addOn.nameDesc);
    setMoreInfoContent(addOn.helpText);
  };

  const handleCancelationPolicyClick = (cancelationPolicyDesc: string): void => {
    setMoreInfoTitle(t`Cancellation Policy`);
    setMoreInfoContent(cancelationPolicyDesc);
  };

  const handleBack = (): void => {
    setMoreInfoTitle(undefined);
    setMoreInfoContent(undefined);
  };

  const getAcceptingNewCustomersLabel = (
    service: EnhancedPersonSitterProfileService
  ): AcceptingNewCustomerProps | null => {
    if (service.isAway) {
      return null;
    }

    if (service.isRepeatCustomersOnly) {
      return {
        text: t`Not accepting new customers.`,
        icon: Rebook,
      };
    }
    if (service.isAcceptingNewOneTimeCustomersOnly) {
      return {
        text: t`Not accepting new customers for weekly care.`,
        icon: RecurringCalendar,
      };
    }
    if (service.isAcceptingNewRecurringClientsOnly) {
      return {
        text: t`Not accepting new customers for one-time care.`,
        icon: Calendar,
      };
    }

    return null;
  };

  const renderService = (service: EnhancedPersonSitterProfileService): JSX.Element => {
    const addons = service.serviceAddOns;
    const isCatOnlyService = isCatOnlyServiceBasedOnAcceptedPetTypes(service);

    // get rates
    const dogRate = addons.filter((sa) => sa.slug === AddOnSlugs.standardRate)[0];
    const catRate = addons.find((sa) => sa.slug === AddOnSlugs.catCare);
    const isCat = (petType === SearchParamsPetType.CAT && catRate) || isCatOnlyService;

    // build service add-ons array
    const filteredAddons = filterAddons(
      addons,
      petType,
      brandPets,
      isCatOnlyService,
      service.slug,
      isSitterInPetCareModesExperiment
    );
    const dogCare = { ...dogRate, nameDesc: t`Dog Care` };
    const serviceAddOns = [
      ...filteredAddons,
      ...(isCat && !isCatOnlyBrand && !isCatOnlyService ? [dogCare] : []),
    ];

    // For cat-only brands, always use cat rate as standard rate
    // Cat care is filtered out in filterAddons since it becomes the standard rate
    const standardRate = (isCatOnlyBrand || isCat ? catRate : dogRate) || dogRate;

    const newCustomersLabel = getAcceptingNewCustomersLabel(service);

    return (
      <Flex flexDirection="column" data-testid="service-full" width="100%">
        <ServiceHeader
          service={service}
          standardRate={standardRate}
          priceTransparencyLevel={priceTransparencyLevel}
          textSize="200"
          priceInCredits={standardRate.priceInCredits}
          showSittersRates={showSittersRates}
        />
        {newCustomersLabel && (
          <Flex justifyContent="space-between" alignItems="center" pt="4x">
            <Paragraph size="100" marginBottom="0x">
              {newCustomersLabel.text}
            </Paragraph>
            <NewCustomersIcon icon={newCustomersLabel.icon} />
          </Flex>
        )}
        {serviceAddOns.length > 0 && (
          <ServiceAddOns
            addOns={serviceAddOns}
            acceptCats={service.acceptsCats || false}
            onMoreInfoClick={handleMoreInfoClick}
            priceTransparencyLevel={priceTransparencyLevel}
            showSittersRates={showSittersRates}
          />
        )}
        {isRevampVersion && (
          <Flex pt="4x" justifyContent="flex-start">
            <RevampCancellationPolicy
              cancellationPolicy={service.meta?.cancellationPolicy}
              serviceName={service.displayName}
              cancellationPolicyDesc={service.meta?.cancellationPolicyDesc}
              onCancelationPolicyClick={handleCancelationPolicyClick}
            />
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(toggleModal)
      ) : (
        <Button
          fullWidth={!isRevampVersion}
          alignSelf={isRevampVersion ? 'flex-start' : 'center'}
          onPress={toggleModal}
          data-testid="see-additional-rates-button"
        >
          {t`See additional rates`}
        </Button>
      )}
      <Modal isOpen={isModalOpen} onClose={toggleModal} data-testid="see-additional-rates-modal">
        <AutoHeight>
          {moreInfoContent && moreInfoTitle ? (
            <AdditionalInfo title={moreInfoTitle} content={moreInfoContent} onBack={handleBack} />
          ) : (
            <>
              <Heading size="300" mb="4x">
                {t`Services`}
              </Heading>
              {servicesToRender.map((service, idx) => (
                <Fragment key={service.slug}>
                  {renderService(service)}
                  <Separator withBorder={idx + 1 !== servicesToRender.length} />
                </Fragment>
              ))}
              {isCtaModal &&
                otherServices.length > 0 &&
                (isMoreServicesOpen ? (
                  otherServices.map((service, idx) => (
                    <Fragment key={service.slug}>
                      {renderService(service)}
                      <Separator withBorder={idx + 1 !== otherServices.length} />
                    </Fragment>
                  ))
                ) : (
                  <Flex pb="8x">
                    <Button
                      variant="flat"
                      iconAlignment="right"
                      onPress={() => {
                        setIsMoreServicesOpen(true);
                      }}
                      childrenFlexGap="4x"
                      icon={MoreIcon}
                    >
                      {plural(otherServices.length, {
                        one: 'View # more service',
                        other: 'View # more services',
                      })}
                    </Button>
                  </Flex>
                ))}
            </>
          )}
        </AutoHeight>
      </Modal>
    </>
  );
};
export default AdditionalRatesSection;
